import { useEffect, useState, type ReactNode } from 'react';
import { X, Clock, User, BookOpen, Users, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import { useAuthStore } from '../../store/authStore';
import { STATUS_CONFIG, type AulaStatus } from './chamada-utils';
import type { HorarioAula } from '../../types/models';

export type AulaDetalhePayload = {
  aula: HorarioAula & { status: AulaStatus; chamadaData?: any };
  turmaNome: string;
  turnoLabel: string;
  matriculados: number;
  materiaNome: string;
  professorNome: string;
  dataReferencia: string;
};

type AlunoLinha = {
  alunoId?: string;
  aluno?: { id: string; nome: string };
  nome?: string;
  statusPresenca?: string;
};

const PRESENCA_COR: Record<string, string> = {
  PRESENTE: '#16a34a',
  AUSENTE: '#dc2626',
  ATRASADO: '#d97706',
  JUSTIFICADO: '#4f46e5',
  DISPENSADO: '#7c3aed',
};

export function AulaDetalheDialog({
  detail,
  onClose,
}: {
  detail: AulaDetalhePayload | null;
  onClose: () => void;
}) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const [alunos, setAlunos] = useState<AlunoLinha[]>([]);
  const [loadingAlunos, setLoadingAlunos] = useState(false);

  const chamadaId = detail?.aula?.chamadaData?.chamadaId as string | undefined;
  const st = detail?.aula?.status;
  const cfg = st ? STATUS_CONFIG[st] : null;

  useEffect(() => {
    if (!detail || !escolaId || !chamadaId) {
      setAlunos([]);
      return;
    }
    let cancelled = false;
    setLoadingAlunos(true);
    (async () => {
      try {
        const { data } = await api.get(ROUTES.chamadas.alunos(escolaId, chamadaId));
        const list = Array.isArray(data) ? data : data?.alunos || data?.data || [];
        if (!cancelled) setAlunos(list);
      } catch {
        if (!cancelled) setAlunos([]);
      } finally {
        if (!cancelled) setLoadingAlunos(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail, escolaId, chamadaId]);

  useEffect(() => {
    if (!detail) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail, onClose]);

  if (!detail) return null;

  const { aula, turmaNome, turnoLabel, matriculados, materiaNome, professorNome, dataReferencia } =
    detail;
  const janela = [aula.horaInicio, aula.horaFim].filter(Boolean).join(' – ');
  const cd = aula.chamadaData;
  const totalP = cd?.totalPresentes ?? 0;
  const totalA = cd?.totalAusentes ?? 0;
  const totalAt = cd?.totalAtrasados ?? 0;
  const totalCh = cd?.totalAlunos ?? totalP + totalA;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Detalhe da aula"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        background: 'rgba(15, 23, 42, 0.45)',
        backdropFilter: 'blur(3px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg-card, #fff)',
          borderRadius: 14,
          width: '100%',
          maxWidth: 440,
          maxHeight: 'min(88vh, 640px)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(15,23,42,0.28)',
          border: '1px solid #e2e8f0',
        }}
      >
        <header
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
            padding: '14px 16px',
            borderBottom: '1px solid #f1f5f9',
            background: '#f8fafc',
          }}
        >
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.62rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {dataReferencia} · {turnoLabel}
            </div>
            <h2 style={{ margin: '4px 0 0', fontSize: '1.05rem', fontWeight: 800, color: '#0f172a' }}>
              {turmaNome}
              {aula.numeroAula ? ` · ${aula.numeroAula}ª aula` : ''}
            </h2>
            {cfg && (
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 6,
                  fontSize: '0.65rem',
                  fontWeight: 800,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: cfg.bg,
                  color: cfg.color,
                }}
              >
                {cfg.label}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            style={{
              border: 'none',
              background: '#e2e8f0',
              borderRadius: 8,
              width: 32,
              height: 32,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#475569',
              flexShrink: 0,
            }}
          >
            <X size={16} />
          </button>
        </header>

        <div style={{ padding: '12px 16px', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'grid', gap: 8 }}>
            <Row icon={<BookOpen size={14} />} label="Matéria" value={materiaNome || 'Não definida'} />
            <Row icon={<User size={14} />} label="Professor" value={professorNome || 'Não definido'} />
            <Row icon={<Clock size={14} />} label="Horário" value={janela || '—'} />
            <Row
              icon={<Users size={14} />}
              label="Turma"
              value={`${matriculados > 0 ? `${matriculados} matriculados` : '—'}`}
            />
          </div>

          {st === 'feita' && cd && (
            <div
              style={{
                marginTop: 14,
                padding: 10,
                borderRadius: 10,
                background: '#f0fdf4',
                border: '1px solid #bbf7d0',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <Metric icon={<CheckCircle2 size={14} color="#16a34a" />} label="Presentes" value={totalP} color="#16a34a" />
              <Metric icon={<AlertTriangle size={14} color="#dc2626" />} label="Ausentes" value={totalA} color="#dc2626" />
              {totalAt > 0 && <Metric label="Atrasados" value={totalAt} color="#d97706" />}
              <Metric label="Na chamada" value={totalCh} color="#0f172a" />
            </div>
          )}

          {st === 'perdida' && (
            <p style={{ margin: '12px 0 0', fontSize: '0.75rem', color: '#991b1b', background: '#fef2f2', padding: '8px 10px', borderRadius: 8 }}>
              Janela da aula encerrou sem chamada concluída.
            </p>
          )}
          {st === 'proxima' && (
            <p style={{ margin: '12px 0 0', fontSize: '0.75rem', color: '#64748b' }}>
              Aula ainda não iniciou no relógio de referência.
            </p>
          )}
          {st === 'agora' && (
            <p style={{ margin: '12px 0 0', fontSize: '0.75rem', color: '#1d4ed8', background: '#eff6ff', padding: '8px 10px', borderRadius: 8 }}>
              Dentro da janela horária — aguardando ou com chamada aberta.
            </p>
          )}

          {chamadaId && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: '0.72rem', fontWeight: 800, color: '#334155', marginBottom: 6 }}>
                Alunos na chamada
              </div>
              {loadingAlunos ? (
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Carregando…</div>
              ) : alunos.length === 0 ? (
                <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Sem lista de alunos nesta chamada.</div>
              ) : (
                <ul
                  style={{
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    maxHeight: 180,
                    overflowY: 'auto',
                    border: '1px solid #e2e8f0',
                    borderRadius: 8,
                  }}
                >
                  {alunos.map((a, i) => {
                    const nome = a.aluno?.nome || a.nome || 'Aluno';
                    const sp = a.statusPresenca || '—';
                    return (
                      <li
                        key={a.alunoId || a.aluno?.id || i}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          padding: '6px 10px',
                          borderBottom: i < alunos.length - 1 ? '1px solid #f1f5f9' : 'none',
                          fontSize: '0.72rem',
                        }}
                      >
                        <span style={{ fontWeight: 600, color: '#0f172a' }}>{nome}</span>
                        <span style={{ fontWeight: 800, color: PRESENCA_COR[sp] || '#64748b' }}>{sp}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}
        </div>

        <footer style={{ padding: '10px 16px', borderTop: '1px solid #f1f5f9' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              padding: '10px 0',
              borderRadius: 10,
              border: '1px solid #e2e8f0',
              background: '#fff',
              fontWeight: 700,
              fontSize: '0.82rem',
              cursor: 'pointer',
              color: '#334155',
            }}
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}

function Row({
  icon,
  label,
  value,
}: {
  icon: ReactNode;
  label: string;
  value: string;
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.78rem' }}>
      <span style={{ color: '#94a3b8', display: 'flex' }}>{icon}</span>
      <span style={{ color: '#64748b', fontWeight: 600, minWidth: 72 }}>{label}</span>
      <span style={{ color: '#0f172a', fontWeight: 700 }}>{value}</span>
    </div>
  );
}

function Metric({
  icon,
  label,
  value,
  color,
}: {
  icon?: ReactNode;
  label: string;
  value: number;
  color: string;
}) {
  return (
    <div style={{ minWidth: 72 }}>
      <div style={{ fontSize: '0.58rem', fontWeight: 700, color: '#64748b', display: 'flex', alignItems: 'center', gap: 3 }}>
        {icon}
        {label}
      </div>
      <div style={{ fontSize: '1.05rem', fontWeight: 800, color }}>{value}</div>
    </div>
  );
}
